import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import LayersIcon from '@mui/icons-material/Layers';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import HealingIcon from '@mui/icons-material/Healing';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';

const QuejasSection = ({
  quejasRecords = [],
  granelRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveQueja
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Auto-fill from bulk records if available
  useEffect(() => {
    if (formData.noLote && formData.noLote !== "manual") {
      const selected = granelRecords.find(r => r.noLote === formData.noLote || r.idContenedor === formData.noLote);
      if (selected) {
        setFormData(prev => ({
          ...prev,
          descripcionProducto: `${selected.categoria} - ${selected.clase} (${selected.tipoMaguey || "Maguey Espadín"})`
        }));
      }
    }
  }, [formData.noLote, granelRecords, setFormData]);

  const handleNewRecord = () => {
    const today = new Date().toISOString().split("T")[0];
    setFormData({
      id: `QUEJA-${Date.now()}`,
      fechaQueja: today,
      noQueja: `QJ-${new Date().getFullYear()}-${String(quejasRecords.length + 1).padStart(3, '0')}`,
      noLote: granelRecords.length > 0 ? (granelRecords[0].noLote || granelRecords[0].idContenedor) : "manual",
      descripcionProducto: "",
      detalleQueja: "",
      fechaAtencion: "",
      medidasCorrectivas: "",
      status: "borrador"
    });
    setIsFormOpen(true);
  };

  const handleEditRecord = (rec) => {
    if (rec.status !== "borrador" && rec.status !== "rechazado") {
      alert("Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.");
      return;
    }
    setFormData({
      id: rec.id,
      fechaQueja: rec.fechaQueja || rec.fechaRecepcion || "",
      noQueja: rec.noQueja || "",
      noLote: rec.noLote || "manual",
      descripcionProducto: rec.descripcionProducto || "",
      detalleQueja: rec.detalleQueja || "",
      fechaAtencion: rec.fechaAtencion || "",
      medidasCorrectivas: rec.medidasCorrectivas || "",
      status: rec.status || "borrador"
    });
    setIsFormOpen(true);
  };

  const handleSubmitForm = (statusToSet = "revision") => {
    const recordToSave = {
      ...formData,
      id: formData.id || `QUEJA-${Date.now()}`,
      status: statusToSet,
      inspector: formData.inspector || (statusToSet === "revision" ? "Enviado a revisión" : "Borrador de usuario")
    };
    handleSaveQueja(recordToSave);
  };

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE LA SECCIÓN */}
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--agave-green)" }}>
            Quejas Acerca del Producto Certificado (Sección IX)
          </h2>
          <p style={{ margin: "4px 0 0 0", color: "var(--text-muted)", fontSize: "13px" }}>
            Reclamaciones de clientes, desviaciones de calidad y acciones correctivas aplicadas
          </p>
        </div>

        <button 
          type="button"
          onClick={handleNewRecord}
          className="btn-add-new"
          style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px" }}
        >
          <AddIcon style={{ fontSize: 20 }} />
          <span style={{ fontWeight: 700 }}>Registrar Nueva Queja</span>
        </button>
      </div>

      {/* TABLA PRINCIPAL DE HISTORIAL */}
      <div className="records-list-wrapper" style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
        <table className="records-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Fecha Queja</th>
              <th>Nº de Queja</th>
              <th>Nº de Lote</th>
              <th>Descripción del Producto</th>
              <th>Detalle de la Queja</th>
              <th>Fecha Atención</th>
              <th>Medidas Correctivas</th>
              <th>Revisor (V.Bo. Inspector)</th>
              <th>Estado</th>
              <th style={{ textAlign: "center" }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {quejasRecords.length === 0 ? (
              <tr>
                <td colSpan="11" style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
                  <ReportProblemIcon style={{ fontSize: 40, opacity: 0.4, marginBottom: "8px" }} />
                  <p style={{ margin: 0, fontSize: "14px" }}>No hay registros de quejas o reclamaciones recibidas.</p>
                </td>
              </tr>
            ) : (
              quejasRecords.map((rec, i) => (
                <tr key={rec.id || i}>
                  <td>{rec.id}</td>
                  <td>{rec.fechaQueja || rec.fechaRecepcion}</td>
                  <td style={{ fontWeight: 800 }}>{rec.noQueja}</td>
                  <td style={{ color: "var(--copper)", fontWeight: 700 }}>{rec.noLote}</td>
                  <td>{rec.descripcionProducto}</td>
                  <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.detalleQueja}>
                    {rec.detalleQueja}
                  </td>
                  <td>{rec.fechaAtencion || "-"}</td>
                  <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.medidasCorrectivas}>
                    {rec.medidasCorrectivas || "-"}
                  </td>
                  <td style={{ fontSize: "11px" }}>{rec.inspector || "Pendiente V.Bo."}</td>
                  <td>
                    <span className={`badge-status ${rec.status}`}>
                      {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <div style={{ display: "flex", justifyContent: "center", gap: "6px" }}>
                      <button 
                        type="button" 
                        onClick={() => setSelectedRecord(rec)}
                        title="Ver Detalle Completo"
                        style={{ background: "rgba(52, 152, 219, 0.15)", color: "#3498db", border: "1px solid rgba(52, 152, 219, 0.3)", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", display: "flex", alignItems: "center" }}
                      >
                        <VisibilityIcon style={{ fontSize: 16 }} />
                      </button>

                      <button 
                        type="button" 
                        onClick={() => handleEditRecord(rec)}
                        disabled={rec.status !== "borrador" && rec.status !== "rechazado"}
                        title={rec.status === "borrador" || rec.status === "rechazado" ? "Editar Registro" : "Solo editables en estado Borrador"}
                        style={{ 
                          background: rec.status === "borrador" || rec.status === "rechazado" ? "rgba(241, 196, 15, 0.15)" : "rgba(255,255,255,0.04)", 
                          color: rec.status === "borrador" || rec.status === "rechazado" ? "#f1c40f" : "var(--text-muted)", 
                          border: rec.status === "borrador" || rec.status === "rechazado" ? "1px solid rgba(241, 196, 15, 0.3)" : "1px solid rgba(255,255,255,0.08)", 
                          borderRadius: "6px", 
                          padding: "5px 8px", 
                          cursor: rec.status === "borrador" || rec.status === "rechazado" ? "pointer" : "not-allowed", 
                          display: "flex", 
                          alignItems: "center",
                          opacity: rec.status === "borrador" || rec.status === "rechazado" ? 1 : 0.4
                        }}
                      >
                        <EditIcon style={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          MODAL 1: FORMULARIO NUEVA QUEJA / EDICIÓN SECCIÓN IX
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "940px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-quejas">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <ReportProblemIcon />
                </div>
                <div>
                  <h3>
                    {formData.id && quejasRecords.some(r => r.id === formData.id)
                      ? "Editar Queja / Reclamación de Producto"
                      : "Nueva Queja / Reclamación de Producto (Sección IX)"}
                  </h3>
                  <p>Registro de incidencias, no conformidades y acciones correctivas</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Formulario en Modal */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitForm("revision");
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px", background: "#F8FAFC" }}
            >
              {/* DETALLES DE LA QUEJA */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(225, 29, 72, 0.12)", color: "#E11D48" }}>
                    <ReportProblemIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      1. Identificación de la Queja
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Fecha, folio e identificación del lote afectado
                    </p>
                  </div>
                </header>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">FECHA DE RECEPCIÓN *</label>
                    <input 
                      type="date" 
                      name="fechaQueja" 
                      required
                      value={formData?.fechaQueja || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                    />
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº DE QUEJA / FOLIO *</label>
                    <input 
                      type="text" 
                      name="noQueja" 
                      required
                      value={formData?.noQueja || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      placeholder="Ej. QJ-2026-001"
                      style={{ fontWeight: 700 }}
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px", marginTop: "10px" }}>
                  <div className="integrated-input-box">
                    <label className="integrated-input-label">Nº DE LOTE AFECTADO</label>
                    <select 
                      name="noLote" 
                      value={formData?.noLote || "manual"} 
                      onChange={handleChange}
                      className="integrated-select-control"
                      style={{ fontWeight: 600 }}
                    >
                      <option value="manual">Ingreso Manual (Lote no listado)</option>
                      {granelRecords.map((r, idx) => (
                        <option key={idx} value={r.noLote || r.idContenedor}>
                          {r.noLote || r.idContenedor} ({r.categoria} - {r.clase})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <label className="integrated-input-label">DESCRIPCIÓN DEL PRODUCTO</label>
                    <input 
                      type="text" 
                      name="descripcionProducto" 
                      value={formData?.descripcionProducto || ""} 
                      onChange={handleChange} 
                      className="integrated-input-control"
                      placeholder="Ej. Mezcal Artesanal Joven Espadín 750ml"
                    />
                  </div>
                </div>

                <div className="integrated-input-box" style={{ marginTop: "10px", minHeight: "85px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">DETALLE DE LA QUEJA *</label>
                  <textarea 
                    name="detalleQueja" 
                    required
                    value={formData?.detalleQueja || ""} 
                    onChange={handleChange} 
                    className="integrated-input-control"
                    style={{ height: "50px", resize: "none" }}
                    placeholder="Describa a detalle el motivo de la reclamación, defecto organoléptico, envasado o reporte..."
                  />
                </div>
              </section>

              {/* ATENCION Y ACCIONES CORRECTIVAS */}
              <section style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                <header style={{ marginBottom: "14px", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(210, 105, 30, 0.12)", color: "var(--copper)" }}>
                    <HealingIcon style={{ fontSize: 20 }} />
                  </div>
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 800, margin: 0, color: "#0F172A", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                      2. Atención y Medidas Correctivas
                    </h4>
                    <p style={{ margin: "2px 0 0 0", color: "#64748B", fontSize: "12px" }}>
                      Seguimiento, fechas de respuesta y acciones implementadas
                    </p>
                  </div>
                </header>

                <div className="integrated-input-box">
                  <label className="integrated-input-label">FECHA DE ATENCIÓN</label>
                  <input 
                    type="date" 
                    name="fechaAtencion" 
                    value={formData?.fechaAtencion || ""} 
                    onChange={handleChange} 
                    className="integrated-input-control"
                  />
                </div>

                <div className="integrated-input-box" style={{ marginTop: "10px", minHeight: "85px", justifyContent: "flex-start", padding: "8px 14px" }}>
                  <label className="integrated-input-label">MEDIDAS CORRECTIVAS IMPLEMENTADAS</label>
                  <textarea 
                    name="medidasCorrectivas" 
                    value={formData?.medidasCorrectivas || ""} 
                    onChange={handleChange} 
                    className="integrated-input-control"
                    style={{ height: "50px", resize: "none" }}
                    placeholder="Describa las acciones correctivas aplicadas en producción o bodega..."
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "6px", borderRadius: "12px", background: "#FFFFFF", padding: "14px 20px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    className="btn-secondary-custom"
                    style={{ padding: "9px 18px", fontSize: "12.5px" }}
                    onClick={() => handleSubmitForm("borrador")}
                  >
                    Guardar Borrador
                  </button>
                  <button
                    type="submit"
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #E11D48 0%, #BE123C 100%)", boxShadow: "0 4px 14px rgba(225, 29, 72, 0.35)" }}
                  >
                    <CheckCircleIcon style={{ fontSize: 18 }} />
                    <span>{formData.id && quejasRecords.some(r => r.id === formData.id) ? "Guardar Cambios" : "Registrar Queja Sección IX"}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL 2: DETALLE COMPLETO DE QUEJAS
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "750px" }}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-quejas">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <ReportProblemIcon />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: "white" }}>
                    Queja Nº: {selectedRecord.noQueja}
                  </h3>
                  <p style={{ margin: "2px 0 0 0", color: "rgba(255,255,255,0.85)", fontSize: "12px" }}>
                    Registro de Quejas Acerca del Producto Certificado (Sección IX)
                  </p>
                </div>
              </div>
              <button 
                type="button"
                className="modal-close-btn"
                onClick={() => setSelectedRecord(null)}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Contenido del Detalle */}
            <div className="detail-modal-body" style={{ padding: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <div>
                  <span style={{ color: "var(--text-muted)", fontSize: "12px", display: "block" }}>Lote Afectado:</span>
                  <span style={{ color: "var(--copper)", fontSize: "16px", fontWeight: 800 }}>{selectedRecord.noLote}</span>
                </div>
                <span className={`badge-status ${selectedRecord.status}`}>
                  {selectedRecord.status === "aprobado" ? "Aprobado" : selectedRecord.status === "revision" ? "En revisión" : "Borrador"}
                </span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", fontSize: "13.5px" }}>
                <div><strong>Fecha de Queja:</strong> {selectedRecord.fechaQueja || selectedRecord.fechaRecepcion}</div>
                <div><strong>Nº de Queja:</strong> {selectedRecord.noQueja}</div>
                <div style={{ gridColumn: "1 / -1" }}><strong>Descripción del Producto:</strong> {selectedRecord.descripcionProducto}</div>
                <div><strong>Fecha de Atención:</strong> {selectedRecord.fechaAtencion || "Pendiente"}</div>
                <div><strong>Inspector Responsable:</strong> {selectedRecord.inspector || "Pendiente V.Bo."}</div>
              </div>

              <div style={{ marginTop: "18px", paddingTop: "14px", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                <strong>Detalle de la Queja:</strong>
                <p style={{ margin: "6px 0 14px 0", color: "#64748B", fontSize: "13px" }}>
                  {selectedRecord.detalleQueja || "Sin detalles registrados."}
                </p>
                <strong>Medidas Correctivas Implementadas:</strong>
                <p style={{ margin: "6px 0 0 0", color: "#64748B", fontSize: "13px" }}>
                  {selectedRecord.medidasCorrectivas || "Sin medidas registradas."}
                </p>
              </div>

              <div className="modal-footer-custom" style={{ marginTop: "20px", borderRadius: "10px" }}>
                <button 
                  type="button"
                  onClick={() => setSelectedRecord(null)} 
                  className="btn-cancel-modal"
                >
                  Cerrar
                </button>
                {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado") && (
                  <button 
                    type="button"
                    onClick={() => {
                      const rec = selectedRecord;
                      setSelectedRecord(null);
                      handleEditRecord(rec);
                    }}
                    className="btn-submit-modal"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", boxShadow: "0 4px 14px rgba(245, 158, 11, 0.3)" }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                    <span>Editar Registro</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default QuejasSection;
